import React from "react";
import { Box } from "@mui/material";
import { NavLink } from "react-router-dom";
import useReveal from "./useReveal";
import PageHero from "./PageHero";
import { CollaborationArt, PlanningArt, IconCheck, IconArrow } from "../Home/landingArt";
import {
  IconCompass,
  IconScale,
  IconHeart,
  IconTarget,
  IconLifebuoy,
  IconSpark,
} from "./marketingArt";
import "../Home/landingpage.scss";
import "./marketing.scss";

/* Product facts we can stand behind — no invented customer counts. */
const FACTS = [
  { figure: "3 steps", label: "From an empty workspace to a running sprint" },
  { figure: "1 place", label: "Board, backlog, reports and the discussion" },
  { figure: "4 roles", label: "Manager, lead, scrum master and developer" },
  { figure: "0", label: "Core capabilities held back for a higher plan" },
];

const VALUES = [
  {
    icon: <IconTarget />,
    title: "Useful beats clever",
    body: "A feature earns its place by removing work from someone's day. If it only looks impressive in a demo, it does not ship.",
  },
  {
    icon: <IconScale />,
    title: "Honest by default",
    body: "Prices, limits and what the product cannot yet do are stated plainly. Nobody should discover a constraint after they have committed.",
  },
  {
    icon: <IconCompass />,
    title: "Opinionated where it helps",
    body: "Scrum works better with some structure. We give you sensible defaults and let you change the parts that are genuinely yours.",
  },
  {
    icon: <IconHeart />,
    title: "Respect the team's attention",
    body: "Notifications that matter, screens that load quickly, and no busywork invented to feed a dashboard.",
  },
  {
    icon: <IconLifebuoy />,
    title: "Support is part of the product",
    body: "Questions reach people who work on Projam. What we learn from them goes straight back into the roadmap.",
  },
  {
    icon: <IconSpark />,
    title: "Ship, then listen",
    body: "We release in small steps and let real usage decide what comes next, rather than betting a quarter on a guess.",
  },
];

const PRINCIPLES = [
  "Delivery data should come from the work itself, never from a status meeting",
  "The reason behind a change belongs next to the change",
  "A plan the team did not shape is a plan the team will not follow",
  "Software for delivery teams should be quick enough to use during a stand-up",
];

const AHEAD = [
  {
    title: "Deeper reporting",
    body: "Velocity trends, cycle time and cross-project views for teams running several squads at once.",
  },
  {
    title: "More of the workflow",
    body: "Richer automation around statuses and assignments, so routine transitions stop being manual.",
  },
  {
    title: "Fits your stack",
    body: "Connections to the tools your team already lives in, so Projam is the plan of record rather than another island.",
  },
];

const About = () => {
  const rootRef = useReveal();

  return (
    <Box className="pj-landing pj-page" ref={rootRef}>
      <PageHero
        eyebrow="About us"
        title={
          <>
            We build the tool we wanted{" "}
            <span className="lp-gradient-text">on our own sprints</span>
          </>
        }
        lead="Projam started with a familiar frustration: the plan lived in one tool, the discussion in another and the truth in somebody's head. We set out to put all three in the same place, and to keep it simple enough that a team would actually use it during a stand-up."
      >
        <NavLink to="/features" className="lp-btn lp-btn--primary">
          Explore the product <IconArrow />
        </NavLink>
        <NavLink to="/contact" className="lp-btn lp-btn--ghost">
          Get in touch
        </NavLink>
      </PageHero>

      {/* ----------------------------------------------------------- facts */}
      <section className="lp-section" style={{ paddingBottom: 0 }}>
        <div className="lp-wrap">
          <div className="pg-facts">
            {FACTS.map((fact, i) => (
              <div
                className="pg-fact"
                key={fact.label}
                data-reveal
                data-reveal-delay={i * 70}
              >
                <strong>{fact.figure}</strong>
                <span>{fact.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------------- story */}
      <section className="lp-section">
        <div className="lp-wrap">
          <div className="lp-showcase">
            <div>
              <span className="lp-eyebrow" data-reveal>
                Our story
              </span>
              <h2 className="lp-h2" data-reveal data-reveal-delay="70">
                Built by a delivery team, for delivery teams
              </h2>
              <p className="lp-lead" data-reveal data-reveal-delay="120">
                We are a software team that ships in sprints, and Projam grew
                out of the way we work. Every screen has been through the test
                that matters: does it hold up on a Monday morning with a sprint
                to plan and a room full of people waiting?
              </p>
              <p className="lp-lead" data-reveal data-reveal-delay="160">
                That is also why the product is deliberately focused. Projam
                does agile delivery properly rather than doing everything
                adequately, and we would rather say no to a feature than hand
                you a tool that needs a manual.
              </p>
              <ul className="lp-checks" data-reveal data-reveal-delay="200">
                {PRINCIPLES.map((item) => (
                  <li key={item}>
                    <span className="lp-check">
                      <IconCheck />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lp-showcase-art" data-reveal data-reveal-delay="120">
              <CollaborationArt />
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- values */}
      <section className="lp-section pg-band-dark">
        <div className="lp-wrap">
          <div className="lp-head">
            <span className="lp-eyebrow" data-reveal>
              What we believe
            </span>
            <h2 className="lp-h2" data-reveal data-reveal-delay="80">
              The rules we hold ourselves to
            </h2>
            <p className="lp-lead" data-reveal data-reveal-delay="140">
              These are not posters on a wall. They decide what gets built,
              what gets cut and how we answer when you ask us something hard.
            </p>
          </div>

          <div className="pg-values">
            {VALUES.map((value, i) => (
              <article
                className="pg-value"
                key={value.title}
                data-reveal
                data-reveal-delay={(i % 3) * 80}
              >
                <div className="pg-value-icon">{value.icon}</div>
                <h3 className="lp-h3">{value.title}</h3>
                <p className="lp-body">{value.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------- roadmap */}
      <section className="lp-section">
        <div className="lp-wrap">
          <div className="lp-showcase is-flipped">
            <div>
              <span className="lp-eyebrow" data-reveal>
                Where we are headed
              </span>
              <h2 className="lp-h2" data-reveal data-reveal-delay="70">
                What we are working on next
              </h2>
              <p className="lp-lead" data-reveal data-reveal-delay="120">
                We build in the open with the teams who use Projam. These are
                the directions we are investing in — and the roadmap moves when
                what we hear from you says it should.
              </p>
              <div className="lp-steps" style={{ gridTemplateColumns: "1fr", marginTop: 28 }}>
                {AHEAD.map((item, i) => (
                  <article
                    className="lp-step"
                    key={item.title}
                    data-reveal
                    data-reveal-delay={i * 90}
                  >
                    <span className="lp-step-num">{i + 1}</span>
                    <h3 className="lp-h3">{item.title}</h3>
                    <p className="lp-body">{item.body}</p>
                  </article>
                ))}
              </div>
            </div>
            <div className="lp-showcase-art" data-reveal data-reveal-delay="120">
              <PlanningArt />
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- cta */}
      <section className="lp-section" style={{ paddingTop: 0 }}>
        <div className="lp-wrap">
          <div className="lp-cta" data-reveal>
            <h2 className="lp-h2">Come and build with us</h2>
            <p>
              Start a project free, or tell us what your team is wrestling with
              — the honest answer is usually more useful than a demo.
            </p>
            <div className="lp-cta-actions">
              <NavLink to="/register" className="lp-btn lp-btn--white">
                Get started free <IconArrow />
              </NavLink>
              <NavLink to="/contact" className="lp-btn lp-btn--light">
                Contact us
              </NavLink>
            </div>
          </div>
        </div>
      </section>
    </Box>
  );
};

export default About;
